import DashboardLayout from "reusableComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "reusableComponents/Navbars/DashboardNavbar";
import Box from "@mui/material/Box";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Pagination from "@mui/material/Pagination";
import {useState, useEffect} from "react";
import {getIssueReporters, getIssues, updateIssueStatus} from "../../axios-client";
import { useTranslation } from "react-i18next";
import StatusBadge from "../../components/SoftBadge/StatusBadge";
import SnackbarNotification from "../../reusableComponents/Snackbar/SnackbarNotification";
import SoftButton from "../../components/SoftButton";
import FiltersCard from "./FiltersCard";
import {
    MenuItem,
    Select,
} from "@mui/material";

const ManageReportedIssues = () => {
    const [issues, setIssues] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [totalPages, setTotalPages] = useState(1);
    const [openImage, setOpenImage] = useState(null);
    const [imageList, setImageList] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const { t, i18n } = useTranslation(["reportIssue", "common"]);
    const [notification, setNotification] = useState({
        show: false,
        snackbarMessage: "",
        severity: "",
    });


    const [emailFilter, setEmailFilter] = useState("")
    const [statusFilter, setStatusFilter] = useState("all")
    const [dateFilter, setDateFilter] = useState({
        start_date: null,
        end_date: null,
    });

    const [allReportersEmails, setAllReportersEmails] = useState([]);

    useEffect(() => {
        setPage(1);
    }, [emailFilter, statusFilter, dateFilter.start_date, dateFilter.end_date]);

    useEffect(() => {
        getIssueReporters()
            .then((res) => {
                const emails = res.data.reporters.map((r) => r.email);
                setAllReportersEmails(emails);
            })
            .catch((err) => {
                console.error("Failed to fetch issue reporters", err);
            });
    }, []);


    const handleClearFilters = () => {
        setEmailFilter("")
        setStatusFilter("all")
        setDateFilter({ start_date: null, end_date: null });
        setPage(1);
    }

    const handleEmailFilterChange = (newEmail) => {
        setEmailFilter(newEmail);
    };

    const handleStatusFilterChange = (newStatus) => {
        setStatusFilter(newStatus);
    };

    const handleDateFilterChange = (newDateFilter) => {
        setDateFilter(newDateFilter);
    };


    const fetchIssues = async () => {
        try {
            const res = await getIssues({
                page,
                pageSize,
                email: emailFilter !== "" ? emailFilter : undefined,
                status: statusFilter !== "all" ? statusFilter : undefined,
                start_date: dateFilter.start_date,
                end_date: dateFilter.end_date,
            });
            setIssues(res.data.issues);
            setTotalPages(res.data.totalPages);
        } catch (error) {
            console.error("Failed to fetch issues", error);
        }
    };

    useEffect(() => {
        fetchIssues();
    }, [page, pageSize, emailFilter, statusFilter, dateFilter]);


    const handleImageClick = (images, index) => {
        setImageList(images);
        setCurrentImageIndex(index);
        setOpenImage(images[index]);
    };

    const handleCloseDialog = () => {
        setOpenImage(null);
        setImageList([]);
        setCurrentImageIndex(0);
    };

    const handleUpdateStatus = (issue, newStatus) => {
        updateIssueStatus(issue.id, newStatus)
            .then(() => {
                setIssues((prevIssues) =>
                    prevIssues.map((item) =>
                        item.id === issue.id ? { ...item, status: newStatus } : item
                    )
                );
                fetchIssues();
            })
            .catch((error) => {
                setNotification({
                    show: true,
                    snackbarMessage: "Failed to update status. Please try again!",
                    severity: "error",
                });
                console.error("Failed to update status", error);
            });
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            {/* Filters Card */}
                <FiltersCard
                    emailFilter={emailFilter}
                    statusFilter={statusFilter}
                    dateFilter={dateFilter}
                    allReportersEmails={allReportersEmails}
                    onEmailFilterChange={handleEmailFilterChange}
                    onStatusFilterChange={handleStatusFilterChange}
                    onDateFilterChange={handleDateFilterChange}
                    onClearFilters={handleClearFilters}
                />

            <SoftBox mt={4}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <SoftBox p={3}>
                            <SoftTypography variant="h5" fontWeight="bold" gutterBottom>
                                {t("manageReportedIssues")}
                            </SoftTypography>

                            {issues.length === 0 ? (
                                <SoftTypography>{t("noIssues")}</SoftTypography>
                            ) : (
                                issues.map((issue) => {
                                    const imageFiles =
                                        issue.files?.filter((f) =>
                                            f.file_type.startsWith("image/")
                                        ) || [];
                                    const nonImageFiles =
                                        issue.files?.filter(
                                            (f) => !f.file_type.startsWith("image/")
                                        ) || [];

                                    return (
                                        <SoftBox
                                            key={issue.id}
                                            mb={2}
                                            p={2}
                                            bgColor="grey-100"
                                            borderRadius="lg"
                                        >
                                            <Box
                                                display="flex"
                                                flexDirection={{ xs: "column", md: "row" }}
                                                gap={2}
                                            >
                                                <Box flex={1}>
                                                    <Box
                                                        display="flex"
                                                    >
                                                        <SoftTypography variant="body1" fontWeight="bold" mr={2}>
                                                            {issue.title}
                                                        </SoftTypography>

                                                        <StatusBadge
                                                            status={issue.status}
                                                            t={t}
                                                            onChangeStatus={(newStatus) =>
                                                                handleUpdateStatus(issue, newStatus)
                                                            }
                                                            canChange={true}
                                                        />
                                                    </Box>

                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        gap={1}
                                                        mt={1}
                                                    >
                                                        <SoftTypography variant="caption" color="text">
                                                            📂{" "}
                                                            {issue.category.charAt(0).toUpperCase() +
                                                                issue.category.slice(1)}
                                                        </SoftTypography>

                                                        <SoftTypography variant="caption" color="text">
                                                            📅{" "}
                                                            {new Date(issue.created_at).toLocaleDateString()}
                                                        </SoftTypography>

                                                        <SoftTypography variant="caption" color="text">
                                                            👤 {issue.user.name} - {issue.user.email}
                                                        </SoftTypography>
                                                    </Box>

                                                    <SoftTypography variant="body2" mt={1}>
                                                        {issue.description}
                                                    </SoftTypography>
                                                </Box>

                                                {imageFiles.length > 0 && <Box
                                                    flexShrink={0}
                                                    minWidth="250px"
                                                    display="flex"
                                                    flexDirection="column"
                                                    gap={1}
                                                    maxWidth="300px"
                                                >
                                                    <SoftTypography
                                                        variant="subtitle2"
                                                        fontWeight="medium"
                                                    >
                                                        📎 {t("attachments")}
                                                    </SoftTypography>

                                                    {nonImageFiles.map((file, idx) => (
                                                        <a
                                                            key={`file-${idx}`}
                                                            href={file.file_path}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: "#007bff",
                                                                fontSize: "14px",
                                                                textDecoration: "none",
                                                            }}
                                                        >
                                                            📄 {t("viewAttachment")} {idx + 1}
                                                        </a>
                                                    ))}

                                                    {imageFiles.length > 0 && (
                                                        <Box display="flex" gap={1}>
                                                            {imageFiles.slice(0, 3).map((file, index) => (
                                                                <Box key={`img-${index}`} position="relative">
                                                                    <img
                                                                        src={file.file_path}
                                                                        alt={`Attachment ${index + 1}`}
                                                                        onClick={() =>
                                                                            handleImageClick(
                                                                                imageFiles.map((f) => f.file_path),
                                                                                index
                                                                            )
                                                                        }
                                                                        style={{
                                                                            width: "80px",
                                                                            height: "80px",
                                                                            objectFit: "cover",
                                                                            borderRadius: "8px",
                                                                            cursor: "pointer",
                                                                            border: "1px solid #ccc",
                                                                        }}
                                                                    />
                                                                    {index === 2 &&
                                                                        imageFiles.length > 3 && (
                                                                            <Box
                                                                                onClick={() =>
                                                                                    handleImageClick(
                                                                                        imageFiles.map((f) => f.file_path),
                                                                                        2
                                                                                    )
                                                                                }
                                                                                position="absolute"
                                                                                top={0}
                                                                                left={0}
                                                                                width="80px"
                                                                                height="80px"
                                                                                display="flex"
                                                                                alignItems="center"
                                                                                justifyContent="center"
                                                                                bgcolor="rgba(0,0,0,0.6)"
                                                                                borderRadius="8px"
                                                                                color="#fff"
                                                                                fontSize="14px"
                                                                                fontWeight="bold"
                                                                                sx={{ cursor: "pointer" }}
                                                                            >
                                                                                +{imageFiles.length - 3}
                                                                            </Box>
                                                                        )}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                                }
                                            </Box>
                                        </SoftBox>
                                    );
                                })
                            )}

                            {issues.length > 0 && (
                                <SoftBox
                                    mt={2}
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    gap={2}
                                    width="100%"
                                >
                                    <SoftBox width="60px">
                                        <Select
                                            id="rowsPerPage"
                                            fullWidth
                                            size="small"
                                            value={pageSize}
                                            onChange={(e) => {
                                                setPageSize(e.target.value);
                                                setPage(1);
                                            }}
                                            sx={{
                                                height: "36px",
                                                fontSize: "13px",
                                                border: "1px solid #d2d6da",
                                                borderRadius: "0.5rem",
                                                backgroundColor: "#fff",

                                                "& .MuiSelect-select": {
                                                    padding: "8px 10px",
                                                },

                                                "& fieldset": {
                                                    border: "none",
                                                },
                                            }}
                                            inputProps={{ "aria-label": "Without label" }}
                                        >
                                            <MenuItem value={3}>3</MenuItem>
                                            <MenuItem value={6}>6</MenuItem>
                                            <MenuItem value={12}>12</MenuItem>
                                        </Select>
                                    </SoftBox>

                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(e, value) => setPage(value)}
                                        color="primary"
                                    />
                                </SoftBox>
                            )}
                        </SoftBox>
                    </Card>
                </Grid>
            </SoftBox>

            <Dialog open={Boolean(openImage)} onClose={handleCloseDialog} maxWidth="md">
                <DialogContent style={{ padding: 0 }}>
                    {imageList.length > 0 && (
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <img
                                src={imageList[currentImageIndex]}
                                alt="Full size"
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    maxHeight: "80vh",
                                    objectFit: "contain",
                                }}
                            />
                            <Box mt={2} display="flex" gap={2} mb={2}>
                                <SoftButton
                                    variant="gradient"
                                    disabled={currentImageIndex === 0}
                                    onClick={() => {
                                        const newIndex = Math.max(currentImageIndex - 1, 0);
                                        setCurrentImageIndex(newIndex);
                                        setOpenImage(imageList[newIndex]);
                                    }}
                                >
                                    ◀ Prev
                                </SoftButton>

                                <SoftButton
                                    variant="gradient"
                                    disabled={currentImageIndex === imageList.length - 1}
                                    onClick={() => {
                                        const newIndex = Math.min(currentImageIndex + 1, imageList.length - 1);
                                        setCurrentImageIndex(newIndex);
                                        setOpenImage(imageList[newIndex]);
                                    }}
                                >
                                    Next ▶
                                </SoftButton>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <SnackbarNotification
                notification={notification}
                setNotification={setNotification}
            />
        </DashboardLayout>
    );
};

export default ManageReportedIssues;
